import { useCallback, useMemo, useState } from 'react';
import useSearchParamsConfig from './useSearchParamsConfig';

export default function useMemberFilters(defaultSearchParams) {
  const filters = useMemo(
    () => ({
      members: [],
      memberBillableTypeId: null,
      memberCostMethods: [],
      memberDisciplines: [],
      memberEmploymentTypes: [],
      memberInvitationStatuses: [],
      memberJobTitles: [],
      memberLevels: [],
      memberLocations: [],
      memberManagers: [],
      memberPractices: [],
      memberSkills: [],
      memberCertifications: [],
      memberStatusId: null,
      memberTags: [],
    }),
    [],
  );

  const [defaultParams] = useState(defaultSearchParams);

  const searchParamsConfig = useSearchParamsConfig();

  const memberSearchParamsConfig = useMemo(
    () => ({
      members: searchParamsConfig.members,
      memberBillableTypeId: searchParamsConfig.memberBillableTypeId,
      memberCostMethods: searchParamsConfig.memberCostMethods,
      memberDisciplines: searchParamsConfig.memberDisciplines,
      memberEmploymentTypes: searchParamsConfig.memberEmploymentTypes,
      memberInvitationStatuses: searchParamsConfig.memberInvitationStatuses,
      memberJobTitles: searchParamsConfig.memberJobTitles,
      memberLevels: searchParamsConfig.memberLevels,
      memberLocations: searchParamsConfig.memberLocations,
      memberManagers: searchParamsConfig.memberManagers,
      memberPractices: searchParamsConfig.memberPractices,
      memberSkills: searchParamsConfig.memberSkills,
      memberCertifications: searchParamsConfig.memberCertifications,
      memberStatusId: searchParamsConfig.memberStatusId,
      memberTags: searchParamsConfig.memberTags,
      ...defaultParams,
    }),
    [searchParamsConfig, defaultParams],
  );

  const mapUrlSearchParams = useCallback(
    (filters) => ({
      memberId: filters.members?.map((v) => v.id),
      memberBillableTypeId: filters.memberBillableTypeId ?? undefined,
      memberCostMethodId: filters.memberCostMethods?.map((v) => v.id),
      memberDisciplineId: filters.memberDisciplines?.map((v) => v.id),
      memberEmploymentTypeId: filters.memberEmploymentTypes?.map((v) => v.id),
      memberInvitationStatusId: filters.memberInvitationStatuses?.map((v) => v.id),
      memberJobTitleId: filters.memberJobTitles?.map((v) => v.id),
      memberLevelId: filters.memberLevels?.map((v) => v.id),
      memberLocationId: filters.memberLocations?.map((v) => v.id),
      memberManagerId: filters.memberManagers?.map((v) => v.id),
      memberPracticeId: filters.memberPractices?.map((v) => v.id),
      memberSkillId: filters.memberSkills?.map((v) => v.id),
      memberCertificationId: filters.memberCertifications?.map((v) => v.id),
      memberStatusId: filters.memberStatusId ?? undefined,
      memberTagId: filters.memberTags?.map((v) => v.id),
    }),
    [],
  );

  const mapClickThroughParams = useCallback(
    (filters) => ({
      member: filters.members?.map((v) => v.id),
      memberBillableTypeId: filters.memberBillableTypeId ?? undefined,
      memberCostMethodId: filters.memberCostMethods?.map((v) => v.id),
      memberDisciplineId: filters.memberDisciplines?.map((v) => v.id),
      memberEmploymentTypeId: filters.memberEmploymentTypes?.map((v) => v.id),
      memberInvitationStatusId: filters.memberInvitationStatuses?.map((v) => v.id),
      memberJobTitleId: filters.memberJobTitles?.map((v) => v.id),
      memberLevelId: filters.memberLevels?.map((v) => v.id),
      memberLocationId: filters.memberLocations?.map((v) => v.id),
      memberManagerId: filters.memberManagers?.map((v) => v.id),
      memberPracticeId: filters.memberPractices?.map((v) => v.id),
      memberSkillId: filters.memberSkills?.map((v) => v.id),
      memberStatusId: filters.memberStatusId ?? undefined,
      memberTagId: filters.memberTags?.map((v) => v.id),
    }),
    [],
  );

  return useMemo(
    () => ({ filters, searchParamsConfig: memberSearchParamsConfig, mapUrlSearchParams, mapClickThroughParams }),
    [filters, memberSearchParamsConfig, mapUrlSearchParams, mapClickThroughParams],
  );
}
